import { BriefDeliverableStatusEnum } from "@entities/brief/lib/constants";
import { useApproveTaskContent } from "@entities/brief/lib/hooks/useApproveTaskContent";
import { useGetBrIefById } from "@entities/brief/lib/hooks/useGetBriefById";
import { useRequestEditTaskContent } from "@entities/brief/lib/hooks/useRequestEditTaskContent";
import { useGetMe } from "@entities/Onboarding/lib/hooks/useGetMe";
import { ToastService } from "@shared/lib/services";
import { IGetScriptingResponse } from "@shared/lib/types/briefCampains";
import { Button } from "@shared/ui";
import { Dropdown, Flex, MenuProps, Space } from "antd";
import {
  Check,
  Download,
  MessageSquare,
  MoreVertical,
  Upload,
  UploadIcon,
} from "lucide-react";
import { useEffect, useMemo } from "react";
import styles from "./styles.module.scss";

interface ScriptFooterProps {
  serverScript?: IGetScriptingResponse;
  briefId: string;
  onDownloadPdf: () => void;
  onRefetch: () => void;
  onSubmitScript?: () => void;
  onUploadScript?: () => void;
  isUploading?: boolean;
}

export function ScriptFooter({
  serverScript,
  briefId,
  onDownloadPdf,
  onRefetch,
  onSubmitScript,
  onUploadScript,
  isUploading,
}: ScriptFooterProps) {
  const { brief } = useGetBrIefById(briefId);
  const taskStatus = serverScript?.campaignInfluencerTaskState?.status;
  const isEditsRequested =
    taskStatus === BriefDeliverableStatusEnum.EditsRequested;
  const isCompleted =
    taskStatus === BriefDeliverableStatusEnum.Completed ||
    taskStatus === BriefDeliverableStatusEnum.Approved;
  const isCancelledOrWithdrawn =
    serverScript?.campaignInfluencer?.isBrandDeclined ||
    serverScript?.campaignInfluencer?.isCreatorDeclined;
  const cancelledLabel = serverScript?.campaignInfluencer?.isBrandDeclined
    ? "Cancelled"
    : "Withdrawn";
  const cancelledReason =
    serverScript?.campaignInfluencer?.brandDeclinedReason ||
    serverScript?.campaignInfluencer?.creatorDeclinedReason;

  const { userDetails } = useGetMe();

  const { approveTaskContent, isApproving, isApproved } = useApproveTaskContent(
    briefId,
    () => {
      onRefetch();
    }
  );

  const { requestEditTaskContent, isRequestingEdit, isRequestedEdit } =
    useRequestEditTaskContent(briefId, () => {
      onRefetch();
    });

  useEffect(() => {
    if (isApproved) {
      ToastService.success("Script approved successfully");
    }
  }, [isApproved]);

  useEffect(() => {
    if (isRequestedEdit) {
      ToastService.success("Changes requested successfully");
    }
  }, [isRequestedEdit]);

  // Count unresolved comments
  const unresolvedCommentCount = useMemo(() => {
    const comments = serverScript?.script?.comments || [];
    return comments.filter((comment) => !comment.isResolved).length;
  }, [serverScript?.script?.comments]);

  // Menu items for dropdown
  const downloadMenuItems: MenuProps["items"] = [
    {
      key: "pdf",
      label: (
        <div className={styles.scriptFooterDropdownItem}>
          <Download size={14} />
          <span>Download Script as PDF</span>
        </div>
      ),
      onClick: onDownloadPdf,
    },
    ...(!isCancelledOrWithdrawn && onUploadScript
      ? [
          {
            key: "upload",
            label: (
              <div className={styles.scriptFooterDropdownItem}>
                <Upload size={14} />
                <span>Upload Script</span>
              </div>
            ),
            onClick: onUploadScript,
          },
        ]
      : []),
  ];

  const renderCta = () => {
    switch (serverScript?.campaignInfluencerTaskState?.status) {
      case BriefDeliverableStatusEnum.Pending:
      case BriefDeliverableStatusEnum.EditsRequested:
        return (
          <Button
            onClick={onSubmitScript}
            icon={<UploadIcon />}
            type="primary"
            loading={isUploading}
            style={{ backgroundColor: "rgb(79, 70, 229)" }}
          >
            Submit Script
          </Button>
        );
      case BriefDeliverableStatusEnum.PendingApproval:
        return (
          <Space>
            <Button
              onClick={() =>
                requestEditTaskContent({
                  data: {
                    campaignInfluencerId:
                      serverScript?.script?.campaignInfluencerId || "",
                    deliverableConfigRefId:
                      serverScript?.campaignInfluencerDeliverableState?._id ||
                      "",
                    taskId:
                      serverScript?.campaignInfluencerTaskState?._id || "",
                  },
                })
              }
              loading={isRequestingEdit}
              icon={<MessageSquare size={14} />}
              disabled={
                !brief?.canInternalTeamTakeOpsActionsOnBehalfOfBrand &&
                !userDetails?.userEntity?.permissions?.includes("super")
              }
            >
              Request Changes
            </Button>
            <Button
              onClick={() =>
                approveTaskContent({
                  data: {
                    campaignInfluencerId:
                      serverScript?.script?.campaignInfluencerId || "",
                    deliverableConfigRefId:
                      serverScript?.campaignInfluencerDeliverableState?._id ||
                      "",
                    taskId:
                      serverScript?.campaignInfluencerTaskState?._id || "",
                  },
                })
              }
              loading={isApproving}
              type="primary"
              icon={<Check size={14} />}
              disabled={
                !brief?.canInternalTeamTakeOpsActionsOnBehalfOfBrand &&
                !userDetails?.userEntity?.permissions?.includes("super")
              }
            >
              Approve Script
            </Button>
          </Space>
        );
      case BriefDeliverableStatusEnum.Completed:
        return (
          <Button type="dashed" disabled>
            Script Completed
          </Button>
        );
      default:
        return null;
    }
  };
  return (
    <div className={styles.scriptFooter}>
      <Flex justify="flex-end" style={{ width: "100%" }}>
        {isCancelledOrWithdrawn ? (
          <div className={styles.script}>
            {/* <div className={styles.scriptFooterLeft} /> */}
            <div className={styles.scriptRight}>
              <span className={styles.scriptStatusText}>
                This Creator has been {cancelledLabel}:
                <span className={styles.scriptMessageReason}>
                  {cancelledReason}
                </span>
              </span>
            </div>
          </div>
        ) : (
          <Space size={10}>
            {renderCta()}
            <Dropdown menu={{ items: downloadMenuItems }} trigger={["click"]}>
              <Button size="small" shape="circle">
                <MoreVertical size={16} />
              </Button>
            </Dropdown>
          </Space>
        )}
      </Flex>
    </div>
  );
}
